import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import {
    Box,
    TextField,
    MenuItem,
    Paper,
    Chip,
    Grid,
    Typography,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Card,
    CardContent,
    Stack,
} from "@mui/material";
import { Delete, CheckCircle, PlayArrow, Refresh, Info } from "@mui/icons-material";
import { useMutation } from "react-query";
import { TaskService } from "../../service/taskService";
import { toast } from "react-toastify";
import { Colors } from "../../styles/theme";
import { useAuth } from "../../hooks/useAuth";
import { usePreferences } from "../../hooks/usePreferences";
import ConfirmationDialog from "../ui/confirmationDialog";
import validLookup from "../../utils/validLookup";
import EnhancedTable from "../enhancedTable/EnhancedTable";
import { formatDateWithPreferences } from "../../utils/dateFormatUtils";
import { useQuery } from "react-query";
import { EmployeeService } from "../../service/employeeService";
import useLocation from "hooks/useLocation";

const getPriorityColor = (priority) => {
    switch (priority) {
        case "high":
            return Colors.danger;
        case "normal":
            return Colors.primary;
        case "low":
            return Colors.muted;
        default:
            return Colors.primary;
    }
};

const getStatusColor = (status) => {
    switch (status) {
        case "completed":
            return Colors.success;
        case "in-progress":
            return Colors.primary;
        case "pending":
            return Colors.warning;
        case "cancelled":
            return Colors.muted;
        default:
            return Colors.dim_grey;
    }
};

const isOverdue = (dueDate, status) => {
    if (!dueDate || status === "completed" || status === "cancelled")
        return false;
    return new Date(dueDate) < new Date();
};

export default function TaskListPage() {
    const { user } = useAuth();
    const { preferences } = usePreferences();
    const [employees, setEmployees] = useState([]);
    const [filters, setFilters] = useState({
        status: "pending",
        assignedToUserId: user?.id || "",
        priority: "",
        dueDateFrom: "",
        dueDateTo: "",
        offset: 0,
        limit: 50,
        sortBy: "dueDate",
    });
    const { availableLocations } = useLocation();

    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        taskId: null,
        action: null,
    });

    const [detailsDialog, setDetailsDialog] = useState({ open: false, task: null });

    // Manual fetch state
    const [tasksState, setTasksState] = useState({ rows: [], count: 0 });
    const [isTasksLoading, setIsTasksLoading] = useState(false);
    const abortCtrlRef = useRef(null);
    // const lastSigRef = useRef("");
    const [reloadSeq, setReloadSeq] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    // Fetch employees for dropdown
    const { data: employeesData } = useQuery(
        ["employees"],
        () => EmployeeService.getAll(),
        {
            enabled: user?.id != null,
            staleTime: 5 * 60 * 1000,
        }
    );

    useEffect(() => {
        if (employeesData?.data) {
            const options = employeesData.data.map(emp => ({
                value: emp.id,
                label: `${emp.firstName} ${emp.lastName}`.trim()
            }))
            setEmployees(options);
        }
    }, [employeesData]);

    //const employees = employeesData?.data || [];

    // Fetch tasks via EnhancedTable handler
    const fetchData = useCallback(({ pageSize, pageIndex, filters, sortBy }) => {
        if (abortCtrlRef.current) {
            try { abortCtrlRef.current.abort(); } catch (_) {}
        }
        const ctrl = new AbortController();
        abortCtrlRef.current = ctrl;

        setIsTasksLoading(true);
        const offset = pageSize * pageIndex;
        const params = { filters, limit: pageSize, offset };
        TaskService.search(params, { signal: ctrl.signal, suppressErrorToast: true })
            .then((resp) => {
                const data = resp?.data?.data || { rows: [], count: 0 };
                const rows = Array.isArray(data) ? data : [];
                setTasksState({ rows, count: data.count || 0 });
                setPageCount(Math.ceil((data.count || 0) / pageSize));
            })
            .catch(() => { /* handled globally */ })
            .finally(() => setIsTasksLoading(false));
    }, [filters]);

    const taskCount = tasksState.count;

    // Mutations
    const updateStatusMutation = useMutation(
        (data) => TaskService.updateStatus(data.taskId, data.status),
        {
            onSuccess: (response) => {
                const { code, message } = { ...response };
                if (code === 0) {
                    toast.success(message || "Task updated");
                    setReloadSeq((s) => s + 1);
                } else {
                    toast.warn(message || "Unable to update task");
                }
            },
            onError: () => {
                toast.error("Something went wrong");
            },
        }
    );

    const deleteMutation = useMutation(
        (taskId) => TaskService.remove(taskId),
        {
            onSuccess: (response) => {
                const { code, message } = { ...response };
                if (code === 0) {
                    toast.success(message || "Task deleted");
                    setReloadSeq((s) => s + 1);
                } else {
                    toast.warn(message || "Unable to delete task");
                }
            },
            onError: () => {
                toast.error("Something went wrong");
            },
        }
    );

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({
            ...prev,
            [field]: value,
            offset: 0,
        }));
    };

    const handleStartWork = (taskId) => {
        updateStatusMutation.mutate({ taskId, status: "in-progress" });
    };

    const handleCompleteTask = (taskId) => {
        setConfirmDialog({
            open: true,
            taskId,
            action: "complete",
        });
    };

    const handleDeleteTask = (taskId) => {
        setConfirmDialog({
            open: true,
            taskId,
            action: "delete",
        });
    };

    const handleConfirmAction = (action) => {
        if (action === validLookup.confirmationDialogAction.ok) {
            if (confirmDialog.action === "complete") {
                updateStatusMutation.mutate({
                    taskId: confirmDialog.taskId,
                    status: "completed",
                });
            } else if (confirmDialog.action === "delete") {
                deleteMutation.mutate(confirmDialog.taskId);
            }
        }
        setConfirmDialog({ open: false, taskId: null, action: null });
    };

    const getConfirmMessage = () => {
        switch (confirmDialog.action) {
            case "complete":
                return "Mark this task as completed?";
            case "delete":
                return "Delete this task?";
            default:
                return "";
        }
    };

    const getTaskEntityDisplay = (task) => {
        if (!task) return '';
        if (task.taskableReference) return task.taskableReference;
        const typeLabel = task.taskableType ? (task.taskableType.charAt(0).toUpperCase() + task.taskableType.slice(1)) : 'Entity';
        return `${typeLabel} #${task.taskableId}`;
    };

    const openDetails = (task) => {
        setDetailsDialog({ open: true, task });
    };

    const closeDetails = () => setDetailsDialog({ open: false, task: null });

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">
                    Task List
                </Typography>
                <IconButton
                    onClick={() => setReloadSeq((s) => s + 1)}
                    disabled={isTasksLoading}
                    title="Refresh task list"
                >
                    <Refresh />
                </IconButton>
            </Box>

            {/* Filters */}
            {/* <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            select
                            size="small"
                            label="Status"
                            value={filters.status}
                            onChange={(e) =>
                                handleFilterChange("status", e.target.value)
                            }
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="in-progress">In Progress</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            select
                            size="small"
                            label="Priority"
                            value={filters.priority}
                            onChange={(e) =>
                                handleFilterChange("priority", e.target.value)
                            }
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="low">Low</MenuItem>
                            <MenuItem value="normal">Normal</MenuItem>
                            <MenuItem value="high">High</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            select
                            size="small"
                            label="Sort By"
                            value={filters.sortBy}
                            onChange={(e) =>
                                handleFilterChange("sortBy", e.target.value)
                            }
                        >
                            <MenuItem value="dueDate">Due Date</MenuItem>
                            <MenuItem value="priority">Priority</MenuItem>
                            <MenuItem value="status">Status</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            type="date"
                            size="small"
                            label="From Date"
                            InputLabelProps={{ shrink: true }}
                            value={filters.dueDateFrom}
                            onChange={(e) =>
                                handleFilterChange("dueDateFrom", e.target.value)
                            }
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            type="date"
                            size="small"
                            label="To Date"
                            InputLabelProps={{ shrink: true }}
                            value={filters.dueDateTo}
                            onChange={(e) =>
                                handleFilterChange("dueDateTo", e.target.value)
                            }
                        />
                    </Grid>
                </Grid>
            </Paper> */}

            {/* Task Count */}
            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color={Colors.dim_grey}>
                    {taskCount} task{taskCount !== 1 ? "s" : ""} found
                </Typography>
            </Box>

            {/* Task Table (EnhancedTable) */}
            <EnhancedTable
                key={reloadSeq}
                showStatusColumn={false}
                columns={useMemo(() => [
                    {
                        Header: 'Title',
                        accessor: 'title',
                        Cell: ({ row }) => (
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {row.original.title}
                                </Typography>
                                {row.original.description && (
                                    <Typography variant="caption" color={Colors.dim_grey}>
                                        {row.original.description.substring(0, 50)}
                                        {row.original.description.length > 50 ? '...' : ''}
                                    </Typography>
                                )}
                            </Box>
                        ),                      
                    },
                    {
                        Header: 'Entity',
                        accessor: (row) => getTaskEntityDisplay(row),
                        id: 'taskable_reference',
                        enableFilter: true,
                        type: 'text'
                    },
                    {
                        Header: 'Priority',
                        accessor: 'priority',
                        Cell: ({ value }) => (
                            <Chip label={value} size="small" sx={{ background: getPriorityColor(value), color: Colors.white }} />
                        ),
                        enableFilter: true,
                        type: 'enum',
                        filterOptions: [
                            { value: 'low', label: 'Low' },
                            { value: 'normal', label: 'Normal' },
                            { value: 'high', label: 'High' }
                        ]
                    },
                    {
                        Header: 'Status',
                        accessor: 'taskStatus',
                        Cell: ({ value }) => (
                            <Chip label={value} size="small" sx={{ background: getStatusColor(value), color: Colors.white }} />
                        ),
                        enableFilter: true,
                        type: 'enum',
                        filterOptions: [
                            { value: 'pending', label: 'Pending' },
                            { value: 'in-progress', label: 'In Progress' },
                            { value: 'completed', label: 'Completed' },
                            { value: 'cancelled', label: 'Cancelled' }
                        ]
                    },
                    {
                        Header: 'Due Date',
                        accessor: 'dueDate',
                        Cell: ({ row }) => {
                            const due = row.original.dueDate;
                            if (!due) {
                                return <Typography variant="body2" color={Colors.dim_grey}>-</Typography>;
                            }
                            const overdue = isOverdue(due, row.original.status);
                            return (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: overdue ? Colors.danger : 'inherit',
                                        fontWeight: overdue ? 600 : 400,
                                    }}
                                >
                                    {formatDateWithPreferences(due, preferences)}
                                    {overdue && ' (Overdue)'}
                                </Typography>
                            );
                        }
                    },
                    {
                        Header: 'Assigned To',
                        accessor: (row) => `${row.assignedToUser?.firstName || ''} ${row.assignedToUser?.lastName || ''}`.trim(),
                        id: 'assignedToUserId',
                        enableFilter: true,
                        type: 'enum',
                        filterOptions: employees,
                    },
                    {
                        Header: 'Location',
                        accessor: (row) => row.location?.name || '-',
                        id: 'locationId',
                        enableFilter: true,
                        type: 'enum',
                        filterOptions: availableLocations.map(loc => ({ value: loc.id, label: loc.name })),
                    },
                ], [employees, availableLocations, preferences])}
                data={tasksState.rows}
                totalRowCount={tasksState.count}
                pageCount={pageCount}
                fetchData={fetchData}
                loading={isTasksLoading}
                selectionMode="none"
                actions={[
                    // {
                    //     icon: <PlayArrow fontSize="small" />,
                    //     onClick: (row) => handleStartWork(row.original.id)
                    // },
                    {
                        icon: <Info fontSize="small" color="info" />,
                        onClick: (row) => openDetails(row)
                    },
                    {
                        icon: <CheckCircle fontSize="small" color="success"/>,
                        onClick: (row) => handleCompleteTask(row.id)
                    },
                    {
                        icon: <Delete fontSize="small" color="danger" />,
                        onClick: (row) => handleDeleteTask(row.id)
                    }
                ]}
            />

            {/* Details Dialog */}
            <Dialog open={detailsDialog.open} onClose={closeDetails} fullWidth maxWidth="sm">
                <DialogTitle>Task Details</DialogTitle>
                <DialogContent>
                    {detailsDialog.task ? (
                        <Card variant="outlined">
                            <CardContent>
                                <Stack spacing={1}>
                                    <Typography variant="h6">{detailsDialog.task.title}</Typography>
                                    <Typography variant="body2" color="textSecondary">{detailsDialog.task.taskableReference || `${detailsDialog.task.taskableType} #${detailsDialog.task.taskableId}`}</Typography>
                                    {detailsDialog.task.description && (
                                        <Typography variant="body2">{detailsDialog.task.description}</Typography>
                                    )}
                                    <Typography variant="caption" color="textSecondary">Assigned To: {detailsDialog.task.assignedToUser ? `${detailsDialog.task.assignedToUser.firstName || ''} ${detailsDialog.task.assignedToUser.lastName || ''}` : 'Unassigned'}</Typography>
                                    <Typography variant="caption" color="textSecondary">Due: {detailsDialog.task.dueDate ? new Date(detailsDialog.task.dueDate).toLocaleDateString() : '-'}</Typography>
                                    <Typography variant="caption" color="textSecondary">Status: {detailsDialog.task.status}</Typography>
                                </Stack>
                            </CardContent>
                        </Card>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDetails}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog */}
            <ConfirmationDialog
                open={confirmDialog.open}
                onClose={() =>
                    setConfirmDialog({ open: false, taskId: null, action: null })
                }
                message={getConfirmMessage()}
                title="Confirm Action"
                listener={handleConfirmAction}
                label={{ positive: "Yes", negative: "No" }}
            />
        </Box>
    );
}
